/*
 * Sign-in.
 *
 * The dashboard is one person's view of one product's numbers, so there is one
 * account, no registration, and no password reset — the credential is set on
 * the Worker with `npm run auth:setup` and changed the same way.
 *
 * What this module holds is a session, not a password: sign in once, and a
 * signed token issued by the Worker sits in localStorage until it expires
 * (30 days, extended in the background while the dashboard is in use). Nothing
 * here is a secret the bundle ships — the deployed files contain no credential
 * of any kind, which is what lets the site sit on a public URL.
 *
 * ── The password never leaves as a password ──────────────────────────────
 *
 * `login()` fetches the KDF parameters, stretches the password with 600k
 * rounds of PBKDF2-SHA256 *here*, and sends the derived key. The Worker stores
 * only a SHA-256 of that key, and gets to do one cheap hash per attempt, which
 * is what keeps a login inside a Worker's 10ms CPU budget. server/src/auth.js
 * has the full reasoning; the short version is that the expensive step has to
 * exist, and this is the only side with the time to run it.
 *
 * Requires a secure context (crypto.subtle is undefined on plain http), which
 * covers the deployed site and localhost, and nothing else.
 */

import { API_BASE } from './api';

export interface Session {
  token: string;
  /** epoch ms. */
  expiresAt: number;
}

const SESSION_KEY = 'pyraxis-dashboard-session';

/**
 * The old shared-token gate's storage key.
 *
 * Cleared on load. That token is the long-lived SNAPSHOT_TOKEN in plaintext, and
 * leaving a superseded copy of it in every browser that ever used the old prompt
 * is a slow leak with no upside.
 */
const LEGACY_TOKEN_KEY = 'pyraxis-dashboard-token';

/** Renew when this much of the session is left, so an open tab never lapses. */
const RENEW_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;

/** Treat a token as expired slightly early, to lose the race with the Worker. */
const CLOCK_SKEW_MS = 60_000;

export class AuthError extends Error {
  /** Seconds until the next attempt is allowed, when rate limited. */
  readonly retryAfter?: number;

  constructor(message: string, retryAfter?: number) {
    super(message);
    this.name = 'AuthError';
    this.retryAfter = retryAfter;
  }
}

/* ── Storage ────────────────────────────────────────────────────────── */

export function getSession(): Session | null {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<Session>;
    if (typeof saved.token !== 'string' || typeof saved.expiresAt !== 'number') return null;
    if (saved.expiresAt - CLOCK_SKEW_MS <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return { token: saved.token, expiresAt: saved.expiresAt };
  } catch {
    return null; // unparseable, or storage disabled in private mode
  }
}

function saveSession(session: Session): Session {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Storage disabled. The session still works for this page's lifetime,
    // because the token is handed back to the caller either way.
  }
  return session;
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Bearer header for the snapshot endpoint, or nothing when signed out. */
export function authHeaders(): Record<string, string> {
  const session = getSession();
  return session ? { Authorization: `Bearer ${session.token}` } : {};
}

/* ── Key derivation ─────────────────────────────────────────────────── */

interface AuthParams {
  iterations: number;
  salt: string;
  keyLength: number;
}

const b64url = (bytes: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

async function deriveProof(password: string, params: AuthParams): Promise<string> {
  if (!crypto?.subtle) {
    throw new AuthError('Sign-in needs a secure connection (https). Open this page over HTTPS.');
  }
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      // The salt is hashed as the *string* the Worker stores, not as decoded
      // bytes. Both sides have to agree, and this is the cheaper convention to
      // keep straight across two languages.
      salt: new TextEncoder().encode(params.salt),
      iterations: params.iterations,
    },
    key,
    params.keyLength,
  );
  return b64url(bits);
}

/* ── Requests ───────────────────────────────────────────────────────── */

/**
 * Sign in, returning the session on success.
 *
 * Every failure arrives as an `AuthError` whose message is meant to be shown
 * verbatim: a person mistyping a password should be told that, and a person
 * whose Worker is unreachable should be told *that* instead of also being told
 * their password is wrong.
 */
export async function login(username: string, password: string): Promise<Session> {
  let params: AuthParams;

  try {
    const res = await fetch(`${API_BASE}/v1/auth/params`);
    if (res.status === 501) {
      throw new AuthError('Sign-in is not set up on the telemetry service yet.');
    }
    if (!res.ok) throw new AuthError(`Service error (${res.status}). Try again in a moment.`);
    params = (await res.json()) as AuthParams;
  } catch (err) {
    if (err instanceof AuthError) throw err;
    throw new AuthError('Could not reach the telemetry service. Check your connection.');
  }

  const proof = await deriveProof(password, params);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), proof }),
    });
  } catch {
    throw new AuthError('Could not reach the telemetry service. Check your connection.');
  }

  if (res.status === 429) {
    const body = (await res.json().catch(() => ({}))) as { retryAfter?: number };
    const seconds = body.retryAfter ?? 900;
    const minutes = Math.ceil(seconds / 60);
    throw new AuthError(
      `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`,
      seconds,
    );
  }
  if (res.status === 401) throw new AuthError('Incorrect username or password.');
  if (!res.ok) throw new AuthError(`Sign-in failed (${res.status}).`);

  const session = (await res.json()) as Session;
  if (!session?.token) throw new AuthError('Sign-in failed: malformed response.');
  return saveSession(session);
}

/**
 * Extend a session that is close to lapsing.
 *
 * Called on load and after each successful snapshot, so a dashboard someone
 * keeps open never logs itself out mid-week. Failure is silent on purpose: the
 * existing token is still valid, and the next attempt is a page load away.
 */
export async function renewIfStale(): Promise<Session | null> {
  const session = getSession();
  if (!session) return null;
  if (session.expiresAt - Date.now() > RENEW_WITHIN_MS) return session;

  try {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.token}` },
    });
    if (!res.ok) return session;
    const next = (await res.json()) as Session;
    return next?.token ? saveSession(next) : session;
  } catch {
    return session;
  }
}
